import {
  DynamicFlowsApiClient,
  type APIRequestOptions,
  type Question as StepQuestion,
} from '@highereducation/dynamic-flows';
import type {
  Step,
  Flow,
  Question,
  DCS,
  MultiQuestion,
} from '../../types/types';
import { QuestionOrientationType, StepType } from '../../types/enums';
import { getApiConfig } from './utils';

const clientCache = new Map<string, DynamicFlowsApiClient>();

function getClient(): DynamicFlowsApiClient {
  const baseUrl = getApiConfig().dynamicFlowUrl;
  let client = clientCache.get(baseUrl);
  if (!client) {
    client = new DynamicFlowsApiClient({ baseUrl });
    clientCache.set(baseUrl, client);
  }
  return client;
}

/**
 * Groups questions by their orientation property.
 * ROW-oriented questions always start a new group; COLUMN-oriented questions
 * are appended to the current group up to `maxPerRow`.
 */
function groupQuestionsByOrientation(questions: Question[], maxPerRow = 3) {
  const groups: { questions: Question[]; indices: number[] }[] = [];
  let currentGroup: { questions: Question[]; indices: number[] } = {
    questions: [],
    indices: [],
  };

  questions.forEach((question, i) => {
    const orientation = question.orientation ?? QuestionOrientationType.COLUMN;
    const startsNewRow = orientation === QuestionOrientationType.ROW;
    const isFirstQuestion = currentGroup.questions.length === 0;
    const groupIsFull = currentGroup.questions.length >= maxPerRow;

    if (startsNewRow || isFirstQuestion || groupIsFull) {
      if (currentGroup.questions.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = { questions: [question], indices: [i] };
    } else {
      currentGroup.questions.push(question);
      currentGroup.indices.push(i);
    }
  });
  if (currentGroup.questions.length > 0) {
    groups.push(currentGroup);
  }
  return groups;
}

/**
 * Collects question IDs that are referenced as children of MULTI_QUESTION steps,
 * so they can be excluded from top-level processing to avoid duplicates.
 */
function collectChildQuestionIds(data: Step[]): Set<string> {
  const ids = new Set<string>();
  for (const step of data) {
    if (
      step.type === StepType.MULTI_QUESTION &&
      Array.isArray((step as MultiQuestion).questions)
    ) {
      for (const q of (step as MultiQuestion).questions!) {
        ids.add(q.questionId);
      }
    }
  }
  return ids;
}

/**
 * Process raw flow data into structured steps
 */
function processFlowData(data: Step[]): Flow {
  const steps: Flow = [];
  const childQuestionIds = collectChildQuestionIds(data);

  const resolveAndGroupQuestions = (
    question: DCS | MultiQuestion,
  ): DCS | MultiQuestion => {
    const questionsArray: Question[] = [];
    if (question && Array.isArray(question.questions)) {
      question.questions.forEach((q) => {
        const questionData = data.find((s) => s.id === q.questionId);
        if (questionData) {
          questionsArray.push({
            ...questionData,
            orientation: q.orientation,
          } as Question);
        }
      });
    }

    const groupedQuestions = groupQuestionsByOrientation(questionsArray);

    return {
      ...question,
      stepQuestions: questionsArray,
      groupedQuestions,
    };
  };

  const processStep = (step: Step): void => {
    if (step.id != null && childQuestionIds.has(step.id)) {
      return;
    }

    switch (step.type) {
      case StepType.MULTI_QUESTION:
        steps.push(resolveAndGroupQuestions(step as MultiQuestion) as Step);
        break;
      case StepType.QUESTION:
      case StepType.DCS:
      case StepType.COMPONENT:
      case StepType.RESULT:
      case StepType.BANNER_CTA:
      case StepType.EXTERNAL_LINK:
        steps.push(step);
        break;
    }
  };

  const entryStep = data.find((step) => step.isEntry === true);

  if (entryStep) {
    processStep(entryStep);
    data.forEach((step) => {
      if (step.id !== entryStep.id) processStep(step);
    });
  } else {
    data.forEach(processStep);
  }

  return steps;
}

const VALID_STEP_TYPES = new Set<string>(Object.values(StepType));

/**
 * Fetch flow steps from the API
 */
export async function getFlow(
  tenantId: string,
  flowId: string,
  requestHeaders: Record<string, string> = {},
  options: APIRequestOptions = { cacheEnabled: false },
): Promise<{ data: Flow | null; error: string | null }> {
  const client = getClient();

  try {
    const flow = await client.getFlow(
      tenantId,
      flowId,
      requestHeaders,
      options,
    );
    const steps = flow.filter((s) => VALID_STEP_TYPES.has(s.type)) as Step[];
    const processedSteps = processFlowData(steps);
    return { data: processedSteps, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch flow';
    return { data: null, error: errorMessage };
  }
}

/** Strip trailing slashes so the request URL is stable across callers. */
const normalizeBase = (baseUrl: string): string => baseUrl.replace(/\/+$/, '');

export type FlowStepsFailureReason =
  | 'invalid_input'
  | 'request_failed'
  | 'empty_flow';

export interface FlowStepsFailure {
  reason: FlowStepsFailureReason;
  error?: unknown;
}

export interface FetchFlowStepsOptions extends APIRequestOptions {
  onFailure?: (failure: FlowStepsFailure) => void;
}

function reportFlowStepsFailure(
  onFailure: FetchFlowStepsOptions['onFailure'],
  failure: FlowStepsFailure,
): void {
  try {
    onFailure?.(failure);
  } catch {
    // Observability must not change the soft-failure contract.
  }
}

/**
 * Fetch and process flow steps against an explicit base URL.
 *
 * Server-side counterpart to `getFlow`, intended for RSC prefetch on routes
 * that know their flow id before render (e.g. `/[tenant]/de/[flowId]`): pass
 * the result to `<DynamicExperience initialSteps=…>` so the first step is in
 * the server-rendered HTML instead of appearing after a client round trip.
 *
 * Why it does not reuse `getFlow`: that path resolves its base URL from the
 * module-level config that `ExperienceConfigProvider` populates on mount. A
 * Server Component never mounts the provider, so the lookup would fall through
 * to the built-in production endpoint — an `int` deploy would silently read
 * prod flows. Mutating that shared module state per request is also unsafe in
 * a server runtime handling many tenants. Taking `baseUrl` from the caller
 * (resolved via `getApiConfigForEnv`) keeps the request env-correct and
 * request-scoped.
 *
 * `init` is forwarded to the request, so callers can pass
 * `{ next: { revalidate } }` and reuse the framework data cache. Soft-fails to
 * `null` so a prefetch miss degrades to the existing client fetch rather than
 * failing the page.
 *
 * `tenantId` must be the same identifier the client sends for this flow. Use
 * `resolveFlowTenantId` on both paths so the source-key, tenant-id, and default
 * fallbacks stay aligned.
 */
export async function fetchFlowSteps(
  baseUrl: string,
  tenantId: string,
  flowId: string,
  init: FetchFlowStepsOptions = {},
): Promise<Flow | null> {
  const { onFailure, ...requestOptions } = init;

  if (!baseUrl || !tenantId || !flowId) {
    reportFlowStepsFailure(onFailure, { reason: 'invalid_input' });
    return null;
  }

  try {
    const client = new DynamicFlowsApiClient({
      baseUrl: normalizeBase(baseUrl),
    });
    const flow = await client.getFlow(tenantId, flowId, {}, requestOptions);
    const steps = flow.filter((s) => VALID_STEP_TYPES.has(s.type)) as Step[];
    const processedSteps = processFlowData(steps);
    if (processedSteps.length === 0) {
      reportFlowStepsFailure(onFailure, { reason: 'empty_flow' });
      return null;
    }
    return processedSteps;
  } catch (error) {
    reportFlowStepsFailure(onFailure, { reason: 'request_failed', error });
    return null;
  }
}

/**
 * Get a single question
 */
export async function getQuestion(
  tenantId: string,
  questionId: string,
): Promise<{ data: StepQuestion | null; error: string | null }> {
  const client = getClient();

  try {
    const data = await client.getQuestion(tenantId, questionId);
    if (!data) {
      return { data: null, error: 'Failed to fetch question' };
    }
    return { data, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch question';
    return { data: null, error: errorMessage };
  }
}
